import RdvCase, { RdvCaseProps } from "./RdvCase";
import _ from "underts";
import {
  AgendaCase,
  AppointmentsId,
  type Dated,
  PatientFlags,
  Timed,
} from "cadendar-shared";
import styles from "./DayRdvs.module.css";
import classNames from "classnames";
import EmptyCase from "./EmptyCase.tsx";

export interface DayRdvsProps<R extends { tag: string }> {
  now: Date;
  rdvs: (Timed &
    Dated &
    (
      | {
          tag: "rdv";
          id: AppointmentsId;
          birthdate: string | null;
          name: string | null;
          forename: string | null;
          object: string | null;
          type: number;
          patient_id: string | null;
          addressed_by: string | null;
          patient_flags: PatientFlags | null;
          confirmation_sent: boolean | null;
          confirmation_sent_at: Date | null;
          confirmation_answer_is_positive: boolean | null;
          confirmation_forced: boolean | null;
        }
      | { tag: "empty"; stars?: number }
      | { tag: "closed" | "closedByHoraire" | "fake" }
    ))[];
  heightMultiplier: number;
  onCaseClick: (
    rdv: R &
      (
        | { tag: "empty"; date: Date; hour: number; minute: number }
        | { tag: "rdv"; id: string }
      )
  ) => void;
  onCloseCrossClick: (id: string) => void;
  onConfirmClick: (id: string) => void;
  onMouseLeave: () => void;
  onMouseEnter: (
    event: React.MouseEvent<HTMLDivElement>,
    rdvId: string
  ) => void;
}

const familyColorsFromRdvs = <
  T extends { hour: number; minute: number; famille_id?: string },
>(
  rdvs: T[]
): (T & { familyColor?: string })[] => {
  const cssColors = [
    "blue",
    "skyblue",
    "aqua",
    "yellow",
    "lightsalmon",
    "fuchsia",
    "slategray",
    "goldenrod",
    "purple",
  ];
  const familyGrouped = _.groupBy(rdvs, "famille_id") as { [key: string]: T[] };
  const colorsApplied = Object.keys(familyGrouped).reduce(
    (result, key, idx) => {
      return {
        ...result,
        [key]:
          key !== "undefined" && key !== "null" && familyGrouped[key].length > 1
            ? familyGrouped[key].map((rdv) => ({
                ...rdv,
                familyColor: cssColors[idx],
              }))
            : [...familyGrouped[key]],
      };
    },
    {} as { [key: string]: (T & { familyColor?: string })[] }
  );
  return Object.values(colorsApplied)
    .flat()
    .sort(
      (rdv1, rdv2) =>
        rdv1.hour * 60 + rdv1.minute - (rdv2.hour * 60 + rdv2.minute)
    );
};

const DayRdvs = <R extends { tag: string }>(props: DayRdvsProps<R>) => {
  const { rdvs, heightMultiplier } = props;
  const { onCaseClick, onCloseCrossClick, onConfirmClick } = props;
  const { onMouseEnter, onMouseLeave } = props;
  const { now } = props;
  const rdvComps = familyColorsFromRdvs(rdvs).map((rdv) => {
    if (rdv.tag === "empty") {
      return (
        <EmptyCase
          key={"DaysRdvsKey_" + (rdv.date.getTime() + rdv.hour + rdv.minute)}
          rdv={rdv}
          heightMultiplier={heightMultiplier}
          onCaseClick={onCaseClick}
        />
      );
    } else {
      return (
        <RdvCase
          rdv={rdv}
          onCaseClick={onCaseClick}
          onCloseCrossClick={onCloseCrossClick}
          onConfirmClick={onConfirmClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          key={
            "id" in rdv
              ? rdv.id
              : "DaysRdvsKey_" + (rdv.date.getTime() + rdv.hour + rdv.minute)
          }
          now={now}
          heightMultiplier={heightMultiplier}
        />
      );
    }
  });
  return (
    <div
      className={classNames("rdvsdujour", styles.paddingTop, styles.aptCol)}
      data-testid={"DayRdvs"}
    >
      {rdvComps}
    </div>
  );
};

export default DayRdvs;
